import { useState, useEffect, useRef, useCallback } from 'react';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

// Click the supplier name → small popover shows contact info.
// Used on distributor-facing pages only (NOT customer-facing — supplier hidden there).
//
// Props:
//   product — the row's product object (needs supplier_name, supplier_phone, supplier_email, supplier_web)
//   children — rendered inline; usually the supplier name. Wrapped in a clickable span.
//   className — optional extra class for the trigger
//
// Usage: wrap the supplier name in <SupplierPopover product={p}>{p.supplier_name}</SupplierPopover>
export default function SupplierPopover({ product, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, openUp: false });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const popoverHeight = 160;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < popoverHeight + 20 && r.top > popoverHeight;
    setPos({
      top: openUp ? r.top - 6 : r.bottom + 6,
      left: r.left,
      openUp,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const closeOnOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const closeOnScroll = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const supplierName = product?.supplier_name || product?.supplier_code || 'Supplier';
  const phone = product?.supplier_phone;
  const email = product?.supplier_email;
  const website = product?.supplier_web;
  const hasContact = !!(phone || email || website);

  // Fallback if children prop is empty/null (defensive — caller should pass the
  // supplier name as children, but if the data shape changes we still render).
  const isEmptyChildren = children == null || children === '' || (Array.isArray(children) && children.length === 0);
  const triggerLabel = isEmptyChildren ? supplierName : children;

  return (
    <>
      <span
        ref={triggerRef}
        onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o); }}
        title="Click to see supplier contact info"
        style={{
          cursor:'pointer',
          textDecoration: open ? 'underline' : 'none',
          color: open ? G : 'inherit',
        }}
        onMouseEnter={(e)=>{ if (!open) e.currentTarget.style.textDecoration='underline'; }}
        onMouseLeave={(e)=>{ if (!open) e.currentTarget.style.textDecoration='none'; }}
      >
        {triggerLabel}
      </span>
      {open && (
        <div
          ref={popoverRef}
          onClick={(e)=>e.stopPropagation()}
          style={{
            position:'fixed',
            top: pos.openUp ? 'auto' : pos.top,
            bottom: pos.openUp ? (window.innerHeight - pos.top) : 'auto',
            left: pos.left,
            zIndex: 1000,
            minWidth: 220,
            maxWidth: 280,
            background:'#fff',
            border:'1px solid #E2E8F0',
            borderRadius: 10,
            boxShadow:'0 8px 32px rgba(15,23,42,0.18)',
            padding: '14px 16px',
            fontFamily:'inherit',
          }}
        >
          <div style={{ fontSize:11, color:'#94A3B8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:4 }}>
            Supplier
          </div>
          <div style={{ fontSize:14, fontWeight:600, color:'#0F172A', marginBottom:10 }}>
            {supplierName}
          </div>
          {hasContact ? (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {phone && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                  <span style={{ color:'#94A3B8', width:14, textAlign:'center' }}>📞</span>
                  <a href={`tel:${phone}`} style={{ color:'#0F172A', textDecoration:'none' }}>
                    {phone}
                  </a>
                </div>
              )}
              {email && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                  <span style={{ color:'#94A3B8', width:14, textAlign:'center' }}>✉</span>
                  <a href={`mailto:${email}`} style={{ color:G, textDecoration:'none', wordBreak:'break-all' }}>
                    {email}
                  </a>
                </div>
              )}
              {website && (
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                  <span style={{ color:'#94A3B8', width:14, textAlign:'center' }}>🌐</span>
                  <a href={website.startsWith('http') ? website : `https://${website}`}
                    target="_blank" rel="noreferrer"
                    style={{ color:G, textDecoration:'none' }}>
                    {website}
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize:11, color:'#94A3B8', fontStyle:'italic' }}>
              No contact info on file.
            </div>
          )}
        </div>
      )}
    </>
  );
}
